import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AlertFormInitializationFragment from '@app/site/secured/rateshopper/rates/alert/fragment/AlertFormInitializationFragment';
import AlertType from '@app/model/alert/AlertType';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import AlertInput from '@app/model/alert/input/AlertInput';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import { useTranslation } from '@app/component/translation/Translation';
import { useForm } from 'react-hook-form';
import { AlertQueryKeys } from '@app/model/alert/service/AlertQueryKeys';
import AlertService from '@app/model/alert/service/AlertService';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import _isEmpty from 'lodash/isEmpty';
import FormMultiSelect from '@app/component/form/input/select/FormMultiSelect';
import FormInputNumber from '@app/component/form/input/text/FormInputNumber';
import { CheckCircleIcon } from '@heroicons/react/20/solid';
import FormInputText from '@app/component/form/input/text/FormInputText';
import FormButton from '@app/component/form/button/FormButton';
import _get from 'lodash/get';
import Range from '@app/model/_common/Range';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import _set from 'lodash/set';
import CompanyHotelParsingSettingState from '@app/model/company_hotel/CompanyHotelParsingSettingState';
import AlertFilterLengthOfStayField from '@app/site/secured/rateshopper/rates/alert/fields/AlertFilterLengthOfStayField';
import AlertScheduleField from '@app/site/secured/rateshopper/rates/alert/fields/AlertScheduleField';
import AlertTimeFrameField from '@app/site/secured/rateshopper/rates/alert/fields/AlertTimeFrameField';
import DataProvider from '@app/model/_common/DataProvider';
import { PROVIDER_DATA } from '@app/component/provider/FindProviderData';
import { AlertRecipientsFragment } from '@app/site/secured/rateshopper/rates/alert/fragment/AlertRecipientsFragment';
import Utilities from '@util/Utilities';
import AlertAddExtraEmailsField from '@app/site/secured/rateshopper/rates/alert/fields/AlertAddExtraEmailsField';
import { AlertRecipientsType } from '@app/model/alert/AlertRecipientsType';
import FormSelect from '@app/component/form/input/select/FormSelect';
import ParityPriceComparisonAlertRuleDifferenceType
	from '@app/model/alert/ParityPriceComparisonAlertRuleDifferenceType';
import FormSwitch from '@app/component/form/input/switch/FormSwitch';
import SwitchLabel from '@app/component/form/input/switch/SwitchLabel';
import { Field } from '@headlessui/react';
import AlertFilterMealsField from '@app/site/secured/rateshopper/rates/alert/fields/AlertFilterMealsField';


const ParityPriceComparisonAlertPage: React.FunctionComponent<{
	alertId: number | undefined,
	isCopy?: boolean
}> = ({ alertId, isCopy = false }) => {

	const { companyHotel } = useSelectedCompanyHotel();

	if (!!companyHotel) {

		return <AlertFormInitializationFragment alertId={alertId}
																						alertType={AlertType.PARITY_PRICE_COMPARISON}
																						companyHotel={companyHotel}
																						isCopy={isCopy} renderForm={(companyHotel, defaultValues) =>
			<CustomParityPriceComparisonAlert companyHotel={companyHotel} defaultValues={defaultValues} />} />;

	}

	return <></>;

};

const CustomParityPriceComparisonAlert: React.FunctionComponent<{
	companyHotel: CompanyHotel,
	defaultValues: AlertInput
}> = ({ companyHotel, defaultValues }) => {

	const toaster = useToaster();
	const { t } = useTranslation();
	const navigate = useNavigate();

	const form = useForm<AlertInput>({ defaultValues });

	const [thresholdRange, setThresholdRange] = useState<Range>({
		from: 1,
		to: 300,
	});


	const queryClient = useQueryClient();
	const saveAlertMutation = useMutation({
		mutationFn: AlertService.saveAlert,
		onSuccess: (data) => {
			toaster.success('Alert', 'Alert saved successfully!');
			queryClient.invalidateQueries({ queryKey: AlertQueryKeys.lists() });
			queryClient.setQueryData(AlertQueryKeys.detail(data.alert.id), data);
			navigate('/secured/rateshopper/rates/alerts');
		},
		onError: (error: string) => toaster.error('Alert', t(error)),
	});


	const onSubmit = (input: AlertInput) => {
		form.clearErrors();

		if (input.recipientsType === AlertRecipientsType.SELECTED && _isEmpty(input.recipientProfileIds)) {
			form.setError('recipientProfileIds', { message: 'Please select at least one recipient!' });
			return;
		}
		if (input.recipientsType === AlertRecipientsType.NONE && _isEmpty(input.extraEmails)) {
			form.setError('extraEmails', { message: 'Please add at least one extra recipient email!' });
			return;
		}
		if (!!input.schedule.weekdays)
			allToEmpty(input.schedule, 'weekdays');
		// @ts-ignore
		input.filter.lengthOfStay = parseInt(input.filter.lengthOfStay);
		// coerce triggerDifference from string -> number
		const pct = _get(input, 'parityPriceComparisonAlertRuleInput.triggerDifference');
		_set(input, 'parityPriceComparisonAlertRuleInput.triggerDifference', parseInt('' + pct, 10));

		saveAlertMutation.mutate(input);

	};


	const allToEmpty = (obj: any, arrayProperty: string) => {
		if (_get(obj, arrayProperty).includes('ALL')) _set(obj, arrayProperty, undefined);
	};

	const isEdit = !!defaultValues.id;

	const activeProviders = Array.from(
		new Set(
			(companyHotel.parsingSettings ?? [])
				.filter((s) => s.state === CompanyHotelParsingSettingState.ACTIVE && s.variables.provider != DataProvider.BRAND_COM)
				.map((s) => s.variables.provider),
		),
	);
	const providerOptions = activeProviders.map((provider) => ({
		id: `option_${provider}`,
		label: PROVIDER_DATA[provider].name,
		value: provider,
	}));

	return <div className="mx-auto max-w-7xl py-6 px-4 sm:px-6 lg:px-8">
		{/* Set Parity Alert */}
		<div className="mx-auto mt-8 lg:mx-0 lg:max-w-none bg-white p-4 sm:p-10 rounded-xl">
			<div className="mb-6">
				<h2 className="text-base font-semibold leading-7 text-gray-900">Add Parity Price Comparison Alert</h2>
				<p className="text-sm leading-6 text-gray-500">
					Configure and personalize your alert in the form provided below.
				</p>
			</div>
			<div className="pt-12 border-t border-gray-200">
				<div className="space-y-12">
					{/* Section - Rulesets */}
					<div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
						<div>
							<h2 className="text-base font-semibold leading-7 text-gray-900">Alert Setup</h2>
							<p className="mt-1 text-sm leading-6 text-gray-600">
								First, choose the providers you want to include in the alert. Then, set the percentage threshold
								that
								will
								trigger the alert.
							</p>
						</div>
						<div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-3 md:col-span-2">
							{/* Providers */}
							<div className="col-span-full">
								{!_isEmpty(companyHotel.parsingSettings) &&
									<FormMultiSelect
										name="parityPriceComparisonAlertRuleInput.providers"
										form={form}
										label="Select Providers"
										placeholder="Select..."
										isRequired
										hideErrorMessage
										disabled={isEdit}
										options={providerOptions} />}
								<Field as="div" className="flex items-center justify-start gap-x-2 mt-2">
									<FormSwitch name="parityPriceComparisonAlertRuleInput.includeGoogleMetasearch"
															label="Google Metasearch"
															form={form}/>
									<SwitchLabel label="Google Metasearch" />
								</Field>
							</div>
							<div className="col-span-1">
								<FormInputNumber name={`parityPriceComparisonAlertRuleInput.triggerDifference`}
																 label={`Trigger Threshold`}
																 form={form}
																 min={thresholdRange.from}
																 max={thresholdRange.to}
																 isRequired
																 disabled={isEdit}
																 hideErrorMessage
																 placeholder="Insert value" />
							</div>
							<div>
								<FormSelect form={form}
														name="parityPriceComparisonAlertRuleInput.differenceType"
														label="Threshold Type"
														size="medium"
														isRequired
														disabled={isEdit}
														options={[
															{
																label: 'Percentage',
																value: ParityPriceComparisonAlertRuleDifferenceType.PERCENTAGE,
																id: 'percentage',
															},
															{
																label: 'Amount',
																value: ParityPriceComparisonAlertRuleDifferenceType.AMOUNT,
																id: 'amount',
															},
														]} />
							</div>
							<div className="col-span-full">
								<Field as="div" className="flex items-center justify-start py-2 gap-x-2">
									<FormSwitch name="parityPriceComparisonAlertRuleInput.ignorePartnerOffers"
															label="Ignore Booking.com Partner Offers"
															form={form}
															disabled={isEdit} />
									<SwitchLabel label="Ignore Booking.com Partner Offers" />
								</Field>
							</div>
							<div className="col-span-full">
								<AlertRuleExplanation companyHotel={companyHotel} form={form} />
							</div>
						</div>
					</div>
					{/* Section - Details */}
					<div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
						<div>
							<h2 className="text-base font-semibold leading-7 text-gray-900">Alert Details</h2>
							<p className="mt-1 text-sm leading-6 text-gray-600">
								Please configure the different details for your alert.
							</p>
						</div>

						<div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
							{/* Length of stay */}
							<div className="col-span-full">
								<AlertFilterLengthOfStayField form={form} />
							</div>
							<div className="col-span-full">
								<AlertFilterMealsField form={form} />
							</div>
						</div>
					</div>
					{/* Section - Timeframes */}
					<div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
						<div>
							<h2 className="text-base font-semibold leading-7 text-gray-900">Timeframe & Frequency</h2>
							<p className="mt-1 text-sm leading-6 text-gray-600">
								Set how often the alert runs and the time period it monitors.
							</p>
						</div>

						<div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
							{/* Frequency */}
							<div className="col-span-full">
								<AlertScheduleField form={form} defaultValues={defaultValues} />
							</div>
							<div className="col-span-full">
								<AlertTimeFrameField form={form} />
							</div>
						</div>
					</div>
					{!companyHotel.settings.alert.disableAlertProfileRecipientsSelection && <AlertRecipientsFragment form={form} />}
					<AlertAddExtraEmailsField form={form} />
					{/* Section - Push Notifications */}
					<div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
						<div>
							<h2 className="text-base font-semibold leading-7 text-gray-900">Finalize Alert</h2>
							<p className="mt-1 text-sm leading-6 text-gray-600">Assign a unique name to your alert. This
								will help you easily identify it
								later.</p>
						</div>
						<div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
							{/* Alert Name */}
							<div className="col-span-full">
								<FormInputText label="Alert Name" name="name" form={form} maxLength={100}
															 placeholder="Insert alert name" isRequired />
							</div>
						</div>
					</div>
				</div>
			</div>
			<div className="flex justify-end my-6 space-x-4">
				<FormButton state="text" to="/secured/rateshopper/rates/alerts"
										isLoading={saveAlertMutation.isPending}>Cancel</FormButton>
				<FormButton state="primary"
										onClick={form.handleSubmit(onSubmit)}
										isLoading={saveAlertMutation.isPending}>{isEdit ? 'Save' : 'Create'} Alert</FormButton>
			</div>
		</div>
	</div>;

};

const AlertRuleExplanation: React.FunctionComponent<{ companyHotel: CompanyHotel, form: any }> = ({
																																																		companyHotel,
																																																		form,
																																																	}) => {

	const ruleInput = form.watch('parityPriceComparisonAlertRuleInput') ?? {};
	const providers = ruleInput.providers as any[]; // TS help
	const trigger = ruleInput.triggerDifference;
	const triggerType = ruleInput.differenceType;

	if (!providers?.length || !trigger) {
		return null;
	}

	return <div className="w-full rounded-lg bg-white ring-1 ring-black ring-opacity-20">
		<div className="p-4">
			<div className="flex items-start">
				<div className="flex-shrink-0">
					<CheckCircleIcon className="h-6 w-6 text-green-400" />
				</div>
				<div className="ml-3 w-0 flex-1 pt-0.5">
					<p className="text-sm font-medium text-gray-900">Your alert translates
						to:</p>
					<p className="mt-1 text-sm text-gray-500">
						This alert will be triggered if your hotel price will
						be higher
						by {trigger}{triggerType === ParityPriceComparisonAlertRuleDifferenceType.PERCENTAGE ? '%' : Utilities.formatCurrency(companyHotel.settings.main.currency)} from
						any
						of the selected providers
					</p>
				</div>
			</div>
		</div>
	</div>;
};

export default ParityPriceComparisonAlertPage;